import React, { useEffect } from 'react'
import SubjectService from 'services/SubjectService'

import { Subject, Subject as SubjectInterface } from 'interfaces/Subject'
import styled from 'styled-components'
import Meta from 'components/modules/SEO/Meta'
import { GetServerSideProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import theme from 'stories/utils/theme'
import { StandardHeadline, StandardSubtitle } from 'stories/elements/Typography/Text'
import { parseLocale } from 'lib/constants/localization'
import Stack from 'components/elements/Stack'
import TaxonomyService from 'services/TaxonomyService'
import { LayerOneTopic } from 'interfaces/Taxonomy'
import SubjectScrollList from 'components/modules/Subjects/SubjectScrollList'
import useMedia from 'lib/hooks/useMedia'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import Breadcrumbs from 'components/modules/SEO/Breadcrumbs'
import StudyNotesIcon from 'assets/taxonomy/taxonomy_study_notes.svg'
import _useTranslation from 'lib/hooks/_useTranslation'
import TaxonomyGrid from 'components/feature/Taxonomy/TaxonomyGrid'
import { seoSubjectsRoute, subjectsTopicRoute, taxonomyLevelOne, taxonomyLevelThree } from 'lib/constants/routes'
import { getPathFromContext } from 'lib/formatting/url'
import { ErrorTypes } from 'lib/constants/ErrorTypes'
import { ApiError } from 'services/ApiService'
import { getTaxonomyCountryConfig } from 'lib/constants/taxonomy/taxonomyConfig'
import Row from 'components/elements/Row'
import FeedKnowCard from 'stories/elements/Know/FeedKnowCard'
import { getDomainByCountryCode } from 'lib/constants/country'
import { captureSentryException } from 'lib/formatting/sentry'

const KnowGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 15px;

    @media (max-width: 1050px) {
        grid-template-columns: 1fr;
    }
`

const Container = styled.div`
    position: relative;
    min-height: 100vh;
    padding: 50px 15px;

    &::after {
        content: '';
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100vh;
        background: linear-gradient(180.02deg, #150620 0.01%, #14183a 50%, #150620 99.99%);
        z-index: -1;
        pointer-events: none;
    }
`

interface Props {
    subjects: SubjectInterface[]
    topicLayerOne: LayerOneTopic
}

function TaxonomySubtopic({ topicLayerOne, subjects }: Props) {
    const { t } = _useTranslation('subjects')
    const { t: tCommon } = _useTranslation('common')
    const md = useMedia(deviceSize.md)

    const { topic, children, subject, knows } = topicLayerOne

    const breadcrumbs = [
        {
            label: tCommon('common/navBarMenuSubjectMenuItem'),
            href: seoSubjectsRoute,
        },
        {
            label: subject.name,
            href: subjectsTopicRoute(subject),
        },
        {
            label: topic.name,
            href: '',
            isNotALink: true,
        },
    ]

    return (
        <Container>
            <section>
                <Meta title={topic.name} description={t('subjects/description', { topic: topic.name })} />

                <Breadcrumbs breadcrumbs={breadcrumbs} />

                <SubjectScrollList
                    subjects={subjects}
                    referrerScreen="taxonomy"
                    subjectOnClickRedirect={(subject) => subjectsTopicRoute(subject)}
                    isDarkTheme
                />

                <Stack gutter={30}>
                    <StandardHeadline
                        textAlign={md ? 'center' : 'left'}
                        color={theme.colors.white}
                        fontFamily="var(--font-inter)"
                        fontWeight={700}
                    >
                        {topic.name}
                    </StandardHeadline>
                    {knows?.length ? (
                        <Stack>
                            <Row>
                                <StudyNotesIcon />
                                <StandardSubtitle fontWeight={600} fontSize={20} color={theme.colors.grayScaleSecondary}>
                                    {t('subjects/bestStudyNotesForSnippet', { knowsCount: knows.length, topic: topic.name })}
                                </StandardSubtitle>
                            </Row>
                            <KnowGrid>
                                {knows.map((know) => (
                                    <FeedKnowCard
                                        knowUuid={know.uuid}
                                        know={know}
                                        key={know.uuid}
                                        referrerScreen="taxonomy"
                                        isDarkTheme
                                    />
                                ))}
                            </KnowGrid>
                        </Stack>
                    ) : null}
                    <Stack gutter={50}>
                        <TaxonomyGrid parentTopic={topic} taxonomy={children} />
                    </Stack>
                </Stack>
            </section>
        </Container>
    )
}

export const getServerSideProps: GetServerSideProps<Props> = async (context) => {
    if (!context.params || typeof context.params.topicLevelOne !== 'string') {
        return { notFound: true }
    }

    const topicIdentifier = context.params.topicLevelOne
    if (topicIdentifier === null) {
        return { notFound: true }
    }

    const translations = await serverSideTranslations(parseLocale(context.locale).interfaceLanguageCode, [
        'common',
        'subjects',
    ])

    const parsedLocale = parseLocale(context.locale)

    const taxonomySubjectsIds = getTaxonomyCountryConfig(context.locale).map((subjects) => subjects.id)

    let topic: LayerOneTopic | null = null

    try {
        topic = await TaxonomyService.getTaxonomyTopicsLayerOne(topicIdentifier)
    } catch (error) {
        if (error instanceof ApiError && error.type === ErrorTypes.NotFound) {
            captureSentryException(error)
            return { notFound: true }
        } else {
            throw error
        }
    }

    if (!topic) {
        captureSentryException('Taxonomy topic level 1 not found')
        return { notFound: true }
    }

    const expectedPath = taxonomyLevelOne(topic.topic.slug)
    const domain = getDomainByCountryCode(topic.subject.countryCode)

    if (topic.subject.countryCode !== parsedLocale.countryCode) {
        return {
            redirect: {
                statusCode: 301,
                destination: `https://${domain}${expectedPath}`,
            },
        }
    }

    if (topic.path.length > 2 && topic.path[2]?.slug) {
        const topicLevelThreePath = taxonomyLevelThree(topic.path[0].slug, topic.path[2].slug)
        return {
            redirect: {
                permanent: true,
                destination: topicLevelThreePath,
            },
        }
    }

    const subjects = await SubjectService.list(parsedLocale.countryCode)

    const actualPath = getPathFromContext(context)

    if (actualPath !== expectedPath) {
        return {
            redirect: {
                permanent: true,
                destination: expectedPath,
            },
        }
    }

    return {
        props: {
            subjects: subjects.filter((subject) => taxonomySubjectsIds.includes(subject.id)) as Subject[],
            topicLayerOne: topic,
            ...translations,
        },
    }
}

export default TaxonomySubtopic
